import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'customer_token';

const CustomerAuthContext = createContext(null);

export const useCustomerAuth = () => {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  return ctx;
};

export const CustomerAuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` }),
    []
  );

  const loadMe = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      setCustomer(null);
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/customer-auth/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.ok) {
        setCustomer(await res.json());
      } else {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setCustomer(null);
      }
    } catch (e) {
      setCustomer(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const requestOtp = async (email) => {
    const res = await fetch(`${API_URL}/api/customer-auth/request-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || 'Could not send code');
    return data;
  };

  const verifyOtp = async (email, otp) => {
    const res = await fetch(`${API_URL}/api/customer-auth/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, otp }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || 'Invalid code');
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setToken(data.access_token);
    await loadMe();
    return data;
  };

  const fetchOrders = async () => {
    const res = await fetch(`${API_URL}/api/customer-auth/orders`, { headers: authHeaders() });
    if (!res.ok) throw new Error('Could not load orders');
    const data = await res.json();
    return data.orders || [];
  };

  const addAddress = async (address) => {
    const res = await fetch(`${API_URL}/api/customer-auth/addresses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(address),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || 'Could not save address');
    setCustomer((c) => (c ? { ...c, addresses: data.addresses } : c));
    return data.addresses;
  };

  const deleteAddress = async (addressId) => {
    const res = await fetch(`${API_URL}/api/customer-auth/addresses/${addressId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) setCustomer((c) => (c ? { ...c, addresses: data.addresses } : c));
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setCustomer(null);
  };

  return (
    <CustomerAuthContext.Provider
      value={{
        token,
        customer,
        loading,
        isAuthenticated: !!customer,
        requestOtp,
        verifyOtp,
        fetchOrders,
        addAddress,
        deleteAddress,
        refresh: loadMe,
        logout,
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};
